import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Chip,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Add,
  Delete
} from '@mui/icons-material';
import axios from 'axios';

const InlineEditableField = ({
  label,
  value,
  fieldPath, // e.g., 'user.firstName' or 'hourlyRate'
  type = 'text', // text, number, email, phone, boolean, select, multiselect, array
  options = [], // for select/multiselect
  maidId,
  onSave,
  adminOnly = true,
  disabled = false,
  multiline = false,
  placeholder = '',
  validation = {}
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [loading, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset edit value when prop value changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditValue(value);
    setError('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    setError('');
  };

  const validateField = (val) => {
    if (validation.required && (!val || val === '')) {
      return 'This field is required';
    }
    if (validation.minLength && val.length < validation.minLength) {
      return `Minimum length is ${validation.minLength}`;
    }
    if (validation.maxLength && val.length > validation.maxLength) {
      return `Maximum length is ${validation.maxLength}`;
    }
    if (validation.min && val < validation.min) {
      return `Minimum value is ${validation.min}`;
    }
    if (validation.max && val > validation.max) {
      return `Maximum value is ${validation.max}`;
    }
    if (validation.pattern && !validation.pattern.test(val)) {
      return validation.patternMessage || 'Invalid format';
    }
    return '';
  };

  const handleSave = async () => {
    // Validate field
    const validationError = validateField(editValue);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

      // Create update object based on field path
      const updateData = {};
      if (fieldPath.includes('.')) {
        const [parentField, childField] = fieldPath.split('.');
        updateData[parentField] = { [childField]: editValue };
      } else {
        updateData[fieldPath] = editValue;
      }

      const response = await axios.put(
        `${apiBase}/api/maids/${maidId}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        onSave(fieldPath, editValue);
        setIsEditing(false);
      } else {
        setError(response.data.error || 'Failed to update field');
      }
    } catch (err) {
      console.error('Error updating field:', err);
      setError(err.response?.data?.error || 'Failed to update field');
    } finally {
      setSaving(false);
    }
  };

  const handleArrayAdd = () => {
    const newArray = Array.isArray(editValue) ? [...editValue] : [];
    newArray.push('');
    setEditValue(newArray);
  };

  const handleArrayRemove = (index) => {
    const newArray = [...editValue];
    newArray.splice(index, 1);
    setEditValue(newArray);
  };

  const handleArrayItemChange = (index, newValue) => {
    const newArray = [...editValue];
    newArray[index] = newValue;
    setEditValue(newArray);
  };

  const renderDisplayValue = () => {
    if (type === 'boolean') {
      return (
        <Chip 
          label={value ? 'Yes' : 'No'} 
          color={value ? 'success' : 'default'}
          size="small"
        />
      );
    }

    if (type === 'array' || Array.isArray(value)) {
      if (!value || value.length === 0) {
        return <Typography variant="body2" color="text.secondary">None</Typography>;
      }
      return (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {value.map((item, index) => (
            <Chip key={index} label={item} size="small" variant="outlined" />
          ))}
        </Box>
      );
    }

    if (type === 'number' && value) {
      return `${value}${label.includes('Rate') ? '/hr' : ''}${label.includes('Distance') || label.includes('Travel') ? ' miles' : ''}`;
    }

    return value || <Typography variant="body2" color="text.secondary">Not specified</Typography>;
  };

  const renderEditField = () => {
    if (type === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={editValue || false}
              onChange={(e) => setEditValue(e.target.checked)}
            />
          }
          label={editValue ? 'Yes' : 'No'}
        />
      );
    }

    if (type === 'select') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            value={editValue || ''}
            onChange={(e) => setEditValue(e.target.value)}
            label={label}
          >
            {options.map(option => (
              <MenuItem key={option.value || option} value={option.value || option}>
                {option.label || option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (type === 'multiselect') {
      return (
        <Autocomplete
          multiple
          options={options}
          value={editValue || []}
          onChange={(e, newValue) => setEditValue(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={index}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={label}
              placeholder={placeholder}
            />
          )}
        />
      );
    }

    if (type === 'array') {
      return (
        <Box>
          {(editValue || []).map((item, index) => (
            <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
              <TextField
                size="small"
                value={item}
                onChange={(e) => handleArrayItemChange(index, e.target.value)}
                placeholder={`${label} ${index + 1}`}
                fullWidth
              />
              <IconButton size="small" onClick={() => handleArrayRemove(index)}>
                <Delete />
              </IconButton>
            </Box>
          ))}
          <IconButton size="small" onClick={handleArrayAdd}>
            <Add />
          </IconButton>
        </Box>
      );
    }

    // Default text/number/email input
    return (
      <TextField
        fullWidth
        size="small"
        type={type === 'number' ? 'number' : type === 'email' ? 'email' : 'text'}
        value={editValue || ''}
        onChange={(e) => setEditValue(e.target.value)}
        placeholder={placeholder}
        multiline={multiline}
        rows={multiline ? 3 : 1}
        inputProps={type === 'number' ? {
          min: validation.min,
          max: validation.max,
          step: validation.step || 'any'
        } : {}}
      />
    );
  };

  if (disabled) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        <Box>
          {renderDisplayValue()}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      
      {isEditing ? (
        <Box>
          {renderEditField()}
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {error}
            </Alert>
          )}
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <Tooltip title="Save changes">
              <IconButton
                size="small"
                color="primary"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? <CircularProgress size={16} /> : <Save />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel editing">
              <IconButton size="small" onClick={handleCancel} disabled={loading}>
                <Cancel />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" gap={1}>
          <Box flex={1}>
            {renderDisplayValue()}
          </Box>
          <Tooltip title={`Edit ${label}`}>
            <IconButton size="small" onClick={handleEdit}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default InlineEditableField;